import React from 'react';

export interface PositioningMapData {
  axisX: { min: string; max: string };
  axisY: { min: string; max: string };
  players: Array<{ name: string; x: number; y: number; isProduct?: boolean }>;
}

interface PositioningMapProps {
  data: PositioningMapData;
}

// SVG chart layout constants
const CHART_PADDING = { top: 28, right: 24, bottom: 28, left: 24 };
const LABEL_MARGIN = 6;
const VIEWBOX_WIDTH = 600;
const VIEWBOX_HEIGHT = 375; // ~16:10 aspect ratio

export const PositioningMap: React.FC<PositioningMapProps> = ({ data }) => {
  const { axisX, axisY, players } = data;

  // Chart area inside the padding
  const chartLeft = CHART_PADDING.left;
  const chartTop = CHART_PADDING.top;
  const chartWidth = VIEWBOX_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
  const chartHeight = VIEWBOX_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;

  // Center of chart area (where axes cross)
  const centerX = chartLeft + chartWidth / 2;
  const centerY = chartTop + chartHeight / 2;

  // Convert data coordinates (0-100) to SVG coordinates
  const toSvgX = (x: number) => chartLeft + (x / 100) * chartWidth;
  const toSvgY = (y: number) => chartTop + chartHeight - (y / 100) * chartHeight; // invert Y

  // Determine label anchor based on position to avoid edge clipping
  const getLabelProps = (
    svgX: number,
    _svgY: number,
    isProduct: boolean
  ): { dx: number; dy: number; textAnchor: 'start' | 'middle' | 'end' } => {
    const r = isProduct ? 8 : 6;
    const offset = r + 5;

    // If dot is in the right 20% of chart, place label to the left
    if (svgX > chartLeft + chartWidth * 0.8) {
      return { dx: -offset, dy: 4, textAnchor: 'end' };
    }
    // If dot is in the left 10%, still place to the right but give extra room
    return { dx: offset, dy: 4, textAnchor: 'start' };
  };

  return (
    <div className="bg-[#f8f8f8] rounded-[16px] p-[16px] my-[16px] border border-[#e8e8e8]">
      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Positioning map"
      >
        {/* Subtle quadrant backgrounds */}
        <rect
          x={chartLeft}
          y={chartTop}
          width={chartWidth / 2}
          height={chartHeight / 2}
          fill="#000000"
          opacity={0.015}
        />
        <rect
          x={centerX}
          y={chartTop}
          width={chartWidth / 2}
          height={chartHeight / 2}
          fill="#000000"
          opacity={0.005}
        />
        <rect
          x={chartLeft}
          y={centerY}
          width={chartWidth / 2}
          height={chartHeight / 2}
          fill="#000000"
          opacity={0.005}
        />
        <rect
          x={centerX}
          y={centerY}
          width={chartWidth / 2}
          height={chartHeight / 2}
          fill="#000000"
          opacity={0.015}
        />

        {/* Horizontal axis (X) */}
        <line
          x1={chartLeft}
          y1={centerY}
          x2={chartLeft + chartWidth}
          y2={centerY}
          stroke="#e0e0e0"
          strokeWidth={1}
        />

        {/* Vertical axis (Y) */}
        <line
          x1={centerX}
          y1={chartTop}
          x2={centerX}
          y2={chartTop + chartHeight}
          stroke="#e0e0e0"
          strokeWidth={1}
        />

        {/* Axis labels */}
        {/* X axis: min (left) */}
        <text
          x={chartLeft}
          y={centerY + 18 + LABEL_MARGIN}
          fontFamily="'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
          fontSize={12}
          fill="#565858"
          textAnchor="start"
        >
          {axisX.min}
        </text>

        {/* X axis: max (right) */}
        <text
          x={chartLeft + chartWidth}
          y={centerY + 18 + LABEL_MARGIN}
          fontFamily="'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
          fontSize={12}
          fill="#565858"
          textAnchor="end"
        >
          {axisX.max}
        </text>

        {/* Y axis: max (top) */}
        <text
          x={centerX + LABEL_MARGIN}
          y={chartTop - LABEL_MARGIN}
          fontFamily="'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
          fontSize={12}
          fill="#565858"
          textAnchor="start"
        >
          {axisY.max}
        </text>

        {/* Y axis: min (bottom) */}
        <text
          x={centerX + LABEL_MARGIN}
          y={chartTop + chartHeight + 16}
          fontFamily="'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
          fontSize={12}
          fill="#565858"
          textAnchor="start"
        >
          {axisY.min}
        </text>

        {/* Glow filter for the product dot */}
        <defs>
          <filter id="product-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={3} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Render competitor dots first (so product renders on top) */}
        {players
          .filter((p) => !p.isProduct)
          .map((player, idx) => {
            const sx = toSvgX(player.x);
            const sy = toSvgY(player.y);
            const labelProps = getLabelProps(sx, sy, false);

            return (
              <g key={`comp-${idx}`}>
                <circle cx={sx} cy={sy} r={6} fill="#565858" />
                <text
                  x={sx}
                  y={sy}
                  dx={labelProps.dx}
                  dy={labelProps.dy}
                  fontFamily="'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
                  fontSize={13}
                  fill="#565858"
                  textAnchor={labelProps.textAnchor}
                >
                  {player.name}
                </text>
              </g>
            );
          })}

        {/* Render product dots on top */}
        {players
          .filter((p) => p.isProduct)
          .map((player, idx) => {
            const sx = toSvgX(player.x);
            const sy = toSvgY(player.y);
            const labelProps = getLabelProps(sx, sy, true);

            return (
              <g key={`prod-${idx}`}>
                <circle
                  cx={sx}
                  cy={sy}
                  r={8}
                  fill="#2197f1"
                  filter="url(#product-glow)"
                />
                <text
                  x={sx}
                  y={sy}
                  dx={labelProps.dx}
                  dy={labelProps.dy}
                  fontFamily="'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
                  fontSize={13}
                  fill="#2197f1"
                  fontWeight="bold"
                  textAnchor={labelProps.textAnchor}
                >
                  {player.name}
                </text>
              </g>
            );
          })}
      </svg>
    </div>
  );
};
